import moment from 'moment';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import notificationsApi from '../../APIs/notifications';
import { ReactComponent as EscLogoSVG } from '../../assets/icons/escape-plan.svg';
import { ReactComponent as FiledTime } from '../../assets/icons/FieldTime.svg';
import {
  Avatar,
  Button,
  Checkbox,
  Divider,
  Loader,
  NavigateBack,
  PageHeader,
  Tabs,
  Typography,
} from '../../designComponents';
import Store from '../../store';
import { RootState } from '../../store/selectors';
import { PaginationType } from '../../types';
import {
  fake_avatar_url,
  humanReadableDateDifference,
  humanReadableDateWithTime,
  requestAPI,
} from '../../utils';
import { notificationTypesRoute } from '../../utils/notificationTypeRouteMapping';
import { StyledButton } from '../Dashboard/style';
import { NotificationCardWrapper, NotificationContainerStyled } from './style';

type TabType = {
  key: string;
  label: string;
};
const tabs: TabType[] = [
  {
    key: 'all',
    label: 'All',
  },
  {
    key: 'last7DaysAgo',
    label: 'Last 7 Days Ago',
  },
  {
    key: 'last15DaysAgo',
    label: 'Last 15 Days Ago',
  },
  {
    key: '1MonthAgo',
    label: '1 Month Ago',
  },
];
type NotificationType = {
  _id: string;
  message: string;
  avatar: string;
  createdAt: string | Date;
  isRead?: boolean;
  notifyFrom?: {};
  meta?: any;
  notificationType: keyof typeof notificationTypesRoute;
  role:
    | 'client'
    | 'technician'
    | 'printer'
    | 'admin'
    | 'draftman'
    | 'fireTeam'
    | 'professional';
};

const Notifications = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>(
    []
  );

  const handleCheckboxChange = (notificationId: string, isChecked: boolean) => {
    setSelectedNotifications((prevSelected) => {
      if (isChecked) {
        return [...prevSelected, notificationId];
      }
      return prevSelected.filter((id) => id !== notificationId);
    });
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };
  const pagination = useSelector(
    (state: RootState) => state.notifications.pagination
  );
  const notificationss = useSelector(
    (state: RootState) => state.notifications.data
  );
  const hasMore = pagination.page < (pagination?.totalPages || 1);

  const handleMarkAsRead = async () => {
    try {
      await requestAPI(
        notificationsApi.readNotifications({
          notificationId: selectedNotifications,
        })
      );
      const updatedNotifications = notificationss.map((notification) => {
        if (selectedNotifications.includes(notification._id)) {
          return {
            ...notification,
            isRead: true,
          };
        }
        return notification;
      });
      dispatch(Store.Actions.set('notifications.data', updatedNotifications));

      setSelectedNotifications([]);
    } catch (error) {
      console.log('Notification fetch page error:', error);
    }
  };

  const handleGetNotifications = async (
    myParams: PaginationType,
    tabKey: string
  ) => {
    try {
      let filterDate: string | undefined;

      switch (tabKey) {
        case 'all':
          filterDate = '';
          break;
        case 'last7DaysAgo':
          filterDate = moment().subtract(7, 'days').format('YYYY/MM/DD');
          break;
        case 'last15DaysAgo':
          filterDate = moment().subtract(15, 'days').format('YYYY/MM/DD');

          break;
        case '1MonthAgo':
          filterDate = moment().subtract(30, 'days').format('YYYY/MM/DD');

          break;
        default:
          filterDate = '';
      }

      await requestAPI(
        notificationsApi.getAllNotifications({
          params: {
            page: myParams.page,
            limit: myParams.limit,
            ...(tabKey !== 'all' && { filterDate: filterDate }),
          },
          action: myParams.page === 1 ? 'set' : 'update',
        })
      );
    } catch (error) {
      console.log('Notification fetch page error:', error);
    }
  };

  useEffect(() => {
    handleGetNotifications(pagination, activeTab);
  }, [pagination, activeTab]);

  const updateLocalNotificationStatus = (id: string) => {
    const updateNotification = notificationss.map((N) => {
      if (N._id === id) {
        return {
          ...N,
          isRead: true,
        };
      }
      return N;
    });
    dispatch(Store.Actions.set('notifications.data', updateNotification));
  };

  const handleReadNotification = async (id: string) => {
    try {
      await requestAPI(
        notificationsApi.readNotifications({
          notificationId: [id],
        })
      );
      updateLocalNotificationStatus(id);
    } catch (error) {
      console.error(error);
    }
  };
  const renderNotifications = (notification: NotificationType) => {
    const isRead = notification.isRead;

    const link = notification.meta
      ? notificationTypesRoute[notification.notificationType](
          notification?.meta
        )
      : undefined;
    return (
      <div
        key={notification?._id}
        onClick={() => {
          if (link) {
            navigate(link);
            if (!isRead) {
              handleReadNotification(notification._id);
            }
          }
        }}
      >
        <div
          className="individual-notification"
          style={{ cursor: link ? 'pointer' : 'default' }}
        >
          <div className="top-column">
            <div className="left-part">
              <div key={notification?._id}>
                <Checkbox
                  checked={selectedNotifications.includes(notification._id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(notification._id, e.target.checked);
                  }}
                  disabled={isRead}
                  style={{ cursor: isRead ? 'not-allowed' : 'pointer' }}
                />
              </div>
              {notification?.notifyFrom ? (
                <Avatar
                  src={
                    notification.avatar ||
                    fake_avatar_url + `?${notification?._id}`
                  }
                  alt="proposal-user"
                  size="md"
                />
              ) : (
                <EscLogoSVG />
              )}
              {isRead ? (
                <Typography
                  className="notificationTitle"
                  dangerouslySetInnerHTML={{ __html: notification.message }}
                />
              ) : (
                <Typography
                  className="notificationTitle"
                  strong
                  dangerouslySetInnerHTML={{ __html: notification.message }}
                />
              )}
            </div>
            <div className="right-part">
              <div
                className="role-color"
                style={{
                  backgroundColor: `${theme.colors[notification.role]?.strong}`,
                }}
              ></div>
              {isRead ? (
                <>
                  <FiledTime className="icon" />
                  <Typography
                    className="createdDate"
                    fontColor="border"
                    variant="smallText"
                  >
                    {humanReadableDateWithTime(notification.createdAt)}
                  </Typography>
                </>
              ) : (
                <>
                  <div className="red-dot"></div>
                  <FiledTime className="icon" />
                  <Typography
                    className="createdDate"
                    fontColor="border"
                    variant="smallText"
                  >
                    {humanReadableDateWithTime(notification.createdAt)}
                  </Typography>
                </>
              )}
            </div>
          </div>
          <div className="bottom-column">
            <div className="actions-wrapper">
              <Typography variant="caption" fontColor="border">
                {humanReadableDateDifference(notification.createdAt)}
              </Typography>
              {isRead ? (
                ''
              ) : (
                <StyledButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReadNotification(notification._id);
                  }}
                >
                  Read this Notification
                </StyledButton>
              )}
            </div>
          </div>
        </div>
        {<Divider spaceY="12px" />}
      </div>
    );
  };

  const handleLoadMore = () => {
    if (hasMore) {
      Store.store.dispatch(
        Store.Actions.set('notifications.pagination', {
          ...pagination,
          page: pagination.page + 1,
        })
      );
    }
  };

  return (
    <NotificationContainerStyled>
      <NavigateBack />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <PageHeader
          pageTitle={
            <Typography style={{ marginLeft: '12px' }}>
              Notifications
            </Typography>
          }
          showDateFilter={false}
          showFilter={false}
          showSearch={false}
        />
        <Button
          onClick={handleMarkAsRead}
          disabled={selectedNotifications.length === 0}
        >
          Mark as Read
        </Button>
      </div>
      <Divider spaceY="12px" />
      <Tabs
        activeKey={activeTab}
        items={tabs}
        onChange={handleTabChange}
        activeStateType="label"
        transparentBackground={true}
        style={{
          position: 'sticky',
          top: '60px',
          backgroundColor: 'white',
          zIndex: 10,
          width: '100%',
          gap: 0,
        }}
      />
      <NotificationCardWrapper>
        <InfiniteScroll
          hasMore={hasMore}
          dataLength={pagination.limit * pagination.page}
          loader={<Loader />}
          next={handleLoadMore}
        >
          {Array.isArray(notificationss) &&
            notificationss.map((notification, idx) => {
              return <div key={idx}>{renderNotifications(notification)}</div>;
            })}
        </InfiniteScroll>
      </NotificationCardWrapper>
    </NotificationContainerStyled>
  );
};
export default Notifications;
